import {Button, Group, SimpleGrid, Stack, Text, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {
  updateAnalysisTemplateMetadata,
  UpdateAnalysisTemplateMetadataParams,
} from 'analysis-tool/templates/AnalysisTemplateActions';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import BenchlingConfig from 'BenchlingConfig';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import ModalFooter from 'ui-library/modals/ModalFooter';
import type {RunModalProps} from 'ui-library/runModal';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';
import {capitalize} from 'util/NameUtil';

export type AnalysisTemplateMetadataModalProps = RunModalProps<void> & {
  analysisTemplate: AnalysisTemplateGraphQL;
};

function MetadataField({label, children}: {children: React.ReactNode; label: string}): JSX.Element {
  return (
    <Stack spacing="xs">
      <Text size="sm" weight={600}>
        {label}
      </Text>
      {children}
    </Stack>
  );
}

export default function AnalysisTemplateMetadataModal({
  analysisTemplate,
  close,
}: AnalysisTemplateMetadataModalProps): JSX.Element {
  const [name, setName] = useState(analysisTemplate.name);
  const [templateCollectionId, setTemplateCollectionId] = useState(analysisTemplate.templateCollection.id);
  const [isSaving, setIsSaving] = useState(false);

  const writable = analysisTemplate.permissions.writable;
  const movable = analysisTemplate.permissions.movable ?? false;
  const canSave = writable || movable;

  const trimmedName = name.trim();
  const canSubmit = !writable || trimmedName.length > 0;

  async function handleSave(): Promise<void> {
    if (!canSave) {
      close();
      return;
    }

    const params: UpdateAnalysisTemplateMetadataParams = {};
    if (writable && trimmedName !== analysisTemplate.name) {
      params.name = trimmedName;
    }
    if (movable && templateCollectionId !== analysisTemplate.templateCollection.id) {
      params.templateCollectionId = templateCollectionId;
    }

    if (Object.keys(params).length === 0) {
      close();
      return;
    }

    setIsSaving(true);
    try {
      await updateAnalysisTemplateMetadata(analysisTemplate, params);
    } finally {
      setIsSaving(false);
    }
    close();
  }

  return (
    <Stack spacing="md" {...getTestProps({component: 'AnalysisTemplateMetadataModal', element: 'root'})}>
      <SimpleGrid cols={2} spacing="md">
        {writable ? (
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
            }}
            data-autofocus={true}
          />
        ) : (
          <MetadataField label="Name">
            <Text>{analysisTemplate.name}</Text>
          </MetadataField>
        )}
        {movable ? (
          <Stack spacing="xs">
            <Text size="sm" weight={600}>
              Template collection
            </Text>
            <TemplateCollectionSelector
              searchable={true}
              value={templateCollectionId}
              onChange={(templateCollection) => {
                setTemplateCollectionId(templateCollection.id);
              }}
            />
          </Stack>
        ) : (
          <MetadataField label="Template collection">
            <Text>{analysisTemplate.templateCollection.name}</Text>
          </MetadataField>
        )}
      </SimpleGrid>
      <SimpleGrid cols={2} spacing="md">
        <MetadataField label="Creator">
          <Text>{analysisTemplate.creator.name}</Text>
        </MetadataField>
        <MetadataField label="Created at">
          <Text>{readableTime(analysisTemplate.createdAt)}</Text>
        </MetadataField>
      </SimpleGrid>
      <SimpleGrid cols={2} spacing="md">
        <MetadataField label="Last modified">
          <Text>{capitalize(fuzzydate(analysisTemplate.modifiedAt))}</Text>
        </MetadataField>
        <MetadataField label="Published at">
          <Text>
            {analysisTemplate.publishedAt != null
              ? readableTime(analysisTemplate.publishedAt)
              : 'Not published'}
          </Text>
        </MetadataField>
      </SimpleGrid>
      <MetadataField label="Step count">
        <Text>
          {analysisTemplate.steps.length} / {BenchlingConfig.PIPELINES_MAX_STEP_COUNT}
        </Text>
      </MetadataField>
      <ModalFooter>
        <Group position="right">
          <Button loading={isSaving} disabled={!canSubmit} onClick={handleSave}>
            {canSave ? 'Save' : 'Done'}
          </Button>
        </Group>
      </ModalFooter>
    </Stack>
  );
}
